import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { InputField } from '../components/InputField';
import { useAuth } from '../contexts/AuthContext';
import './ProfilePage.css';

interface EditProfileData {
  full_name: string;
  phone_number: string;
  email: string;
}

export const ProfilePage: React.FC = () => {
  const { user, profile, signOut, refreshProfile, loading, initialized } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editProfile, setEditProfile] = useState<EditProfileData>({
    full_name: profile?.full_name || '',
    phone_number: profile?.phone_number || '',
    email: profile?.email || '',
  });
  const [showSignInView, setShowSignInView] = useState(false);

  // Update editProfile when profile changes
  React.useEffect(() => {
    if (profile) {
      setEditProfile({
        full_name: profile.full_name || '',
        phone_number: profile.phone_number || '',
        email: profile.email || '',
      });
    }
  }, [profile]);

  // Stabilize the sign-in view to prevent flickering
  React.useEffect(() => {
    if (initialized && !loading) {
      // Add a small delay before showing sign-in view to prevent flickering
      const timer = setTimeout(() => {
        setShowSignInView(!profile);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [initialized, loading, profile]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditProfile({
      full_name: profile?.full_name || '',
      phone_number: profile?.phone_number || '',
      email: profile?.email || '',
    });
  };

  const handleSave = async () => {
    if (!profile) return;
    
    try {
      // TODO: Implement profile update with Supabase
      // await updateProfile(profile.id, editProfile);
      alert('Profile updated successfully!');
      setIsEditing(false);
      await refreshProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditProfile({
      full_name: profile?.full_name || '',
      phone_number: profile?.phone_number || '',
      email: profile?.email || '',
    });
  };

  const handleMyRequests = () => {
    alert('This feature will show your request history. Coming soon!');
  };

  const handleTechnicianDashboard = () => {
    // This will be handled by the Link component
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      try {
        await signOut();
      } catch (error) {
        console.error('Logout error:', error);
        alert('Failed to logout. Please try again.');
      }
    }
  };

  // Check if user is a technician
  const isTechnician = user?.user_metadata?.role === 'technician' || profile?.role === 'technician';

  const menuItems = [
    // Show Technician Dashboard for technicians, My Requests for regular users
    ...(isTechnician ? [{
      id: 'technician-dashboard',
      title: 'Technician Dashboard',
      icon: 'fas fa-tools',
      onPress: handleTechnicianDashboard,
      isLink: true,
      linkTo: '/technician',
    }] : [{
      id: 'requests',
      title: 'My Requests',
      icon: 'fas fa-list',
      onPress: handleMyRequests,
    }]),
    {
      id: 'settings',
      title: 'Settings',
      icon: 'fas fa-cog',
      onPress: () => alert('Settings feature coming soon!'),
    },
    {
      id: 'help',
      title: 'Help & Support',
      icon: 'fas fa-question-circle',
      onPress: () => alert('Contact support at (555) 123-4567'),
    },
    {
      id: 'about',
      title: 'About',
      icon: 'fas fa-info-circle',
      onPress: () => alert('On My Way Auto v1.0.0'),
    },
  ];

  // Show loading state while checking authentication
  if (loading || !initialized) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <h1 className="profile-title">Profile</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '16px' }}></i>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <h1 className="profile-title">Profile</h1>
      </div>

      {profile && !showSignInView ? (
        // User is signed in - show full profile
        <>
          <div className="profile-section">
            <div className="avatar-container">
              <div className="avatar">
                <i className="fas fa-user"></i>
              </div>
              <h2 className="user-name">{profile.full_name}</h2>
              <p className="member-since">
                Member since {new Date(profile.created_at).toLocaleDateString()}
              </p>
            </div>

            <div className="stats-container">
              <div className="stat-item">
                <span className="stat-number">0</span>
                <span className="stat-label">Total Requests</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">4.9</span>
                <span className="stat-label">Rating</span>
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section-title">Personal Information</h3>
            
            {isEditing ? (
              <div className="edit-form">
                <InputField
                  label="Full Name"
                  value={editProfile.full_name}
                  onChangeText={(text) => setEditProfile(prev => ({ ...prev, full_name: text }))}
                  placeholder="Enter your full name"
                />
                <InputField
                  label="Phone Number"
                  value={editProfile.phone_number}
                  onChangeText={(text) => setEditProfile(prev => ({ ...prev, phone_number: text }))}
                  placeholder="Enter your phone number"
                  keyboardType="phone-pad"
                />
                <InputField
                  label="Email Address"
                  value={editProfile.email}
                  onChangeText={(text) => setEditProfile(prev => ({ ...prev, email: text }))}
                  placeholder="Enter your email address"
                  keyboardType="email-address"
                />
                
                <div className="edit-buttons">
                  <Button
                    title="Save"
                    onPress={handleSave}
                    variant="primary"
                    style={{ flex: 1, marginRight: '6px' }}
                  />
                  <Button
                    title="Cancel"
                    onPress={handleCancel}
                    variant="outline"
                    style={{ flex: 1, marginLeft: '6px' }}
                  />
                </div>
              </div>
            ) : (
              <div className="info-container">
                <div className="info-row">
                  <i className="fas fa-user"></i>
                  <span className="info-text">{profile.full_name}</span>
                </div>
                <div className="info-row">
                  <i className="fas fa-phone"></i>
                  <span className="info-text">{profile.phone_number}</span>
                </div>
                <div className="info-row">
                  <i className="fas fa-envelope"></i>
                  <span className="info-text">{profile.email}</span>
                </div>
                
                <Button
                  title="Edit Profile"
                  onPress={handleEdit}
                  variant="outline"
                  style={{ marginTop: '16px' }}
                />
              </div>
            )}
          </div>

          <div className="section">
            <h3 className="section-title">Menu</h3>
            {menuItems.map((item) => {
              if (item.isLink) {
                return (
                  <Link
                    key={item.id}
                    to={item.linkTo}
                    className="menu-item"
                  >
                    <div className="menu-item-left">
                      <i className={item.icon}></i>
                      <span className="menu-item-text">{item.title}</span>
                    </div>
                    <i className="fas fa-chevron-right"></i>
                  </Link>
                );
              }
              
              return (
                <div
                  key={item.id}
                  className="menu-item"
                  onClick={item.onPress}
                >
                  <div className="menu-item-left">
                    <i className={item.icon}></i>
                    <span className="menu-item-text">{item.title}</span>
                  </div>
                  <i className="fas fa-chevron-right"></i>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        // User is not signed in - show simple welcome screen
        <div className="welcome-section">
          <div className="welcome-icon">
            <i className="fas fa-car"></i>
          </div>
          <h2 className="welcome-title">Welcome to On My Way Auto</h2>
          <p className="welcome-description">
            Get started by creating an account to access roadside assistance services, track your requests, and manage your profile.
          </p>
          <div className="benefits-list">
            <div className="benefit-item">
              <i className="fas fa-check-circle"></i>
              <span className="benefit-text">24/7 Roadside Assistance</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check-circle"></i>
              <span className="benefit-text">Real-time Technician Tracking</span>
            </div>
            <div className="benefit-item">
              <i className="fas fa-check-circle"></i>
              <span className="benefit-text">Request History & Management</span>
            </div>
          </div>
        </div>
      )}



      <div className="logout-section">
        {profile && !showSignInView ? (
          <Button
            title="Logout"
            onPress={handleLogout}
            variant="outline"
            style={{ borderColor: '#F44336', color: '#F44336' }}
          />
        ) : (
          <div className="auth-buttons">
            <Link to="/signup">
              <Button
                title="Sign Up"
                onPress={() => {}}
                variant="primary"
                style={{ width: '100%', marginBottom: '12px' }}
              />
            </Link>
            <Link to="/login">
              <Button
                title="Sign In"
                onPress={() => {}}
                variant="outline"
                style={{ width: '100%' }}
              />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};
